import { createAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';

import { toStatsError } from '../../../utils/statistics/errors';
import billingService from './billingService';

export const BILLING_MODULE_KEY = 'billing';

const entityStore = (state) => state[BILLING_MODULE_KEY];

export const selectors = {
  selectBillingProfile: createSelector(entityStore, (state) => state.data),
  selectPaymentCard: createSelector(entityStore, (state) => state.paymentCard),
  selectGetPaymentCardStatus: createSelector(entityStore, (state) => state.status.getPaymentCard),
  selectGetPaymentCardUpdateLinkStatus: createSelector(
    entityStore,
    (state) => state.status.getPaymentCardUpdateLink
  ),
  selectCurrentSubscription: createSelector(entityStore, (state) => state.subscription),
  selectServerTime: createSelector(entityStore, (state) => state.serverTime),
  selectTariffCatalog: createSelector(entityStore, (state) => state.tariffCatalog),
  selectTariffs: createSelector(entityStore, (state) => state.tariffCatalog.tariffs),
  selectTariffMeta: createSelector(entityStore, (state) => state.tariffCatalog.meta),
  selectError: createSelector(entityStore, (state) => state.error),
  selectGetBillingProfileStatus: createSelector(entityStore, (state) => state.status.getBillingProfile),
  selectCreateBillingProfileStatus: createSelector(entityStore, (state) => state.status.createBillingProfile),
  selectRequestCustomPlanStatus: createSelector(entityStore, (state) => state.status.requestCustomPlan),
  selectGetCurrentSubscriptionStatus: createSelector(
    entityStore,
    (state) => state.status.getCurrentSubscription
  ),
  selectGetTariffsStatus: createSelector(entityStore, (state) => state.status.getTariffs),
  selectPaymentData: createSelector(entityStore, (state) => state.paymentData),
  selectCreateSubscriptionStatus: createSelector(entityStore, (state) => state.status.createSubscription),
  selectUpgradeSubscriptionStatus: createSelector(entityStore, (state) => state.status.upgradeSubscription),
  selectDowngradeSubscriptionStatus: createSelector(
    entityStore,
    (state) => state.status.downgradeSubscription
  ),
  selectCancelSubscriptionStatus: createSelector(entityStore, (state) => state.status.cancelSubscription),
  selectRenewSubscriptionStatus: createSelector(entityStore, (state) => state.status.renewSubscription),
  selectPayInvoiceStatus: createSelector(entityStore, (state) => state.status.payInvoice),
  selectDepositStatus: createSelector(entityStore, (state) => state.status.deposit),
  selectInvoices: createSelector(entityStore, (state) => state.invoices),
  selectInvoicesPage: createSelector(entityStore, (state) => state.invoicesPage),
  selectGetInvoicesPageStatus: createSelector(entityStore, (state) => state.status.getInvoicesPage),
  selectUsage: createSelector(entityStore, (state) => state.usage),
  selectGetUsageStatus: createSelector(entityStore, (state) => state.status.getUsage),
  selectUsageError: createSelector(entityStore, (state) => state.usageError),
  selectPeriodStatements: createSelector(entityStore, (state) => state.periodStatements),
  selectGetPeriodStatementsStatus: createSelector(entityStore, (state) => state.status.getPeriodStatements),
  selectPeriodStatementsError: createSelector(entityStore, (state) => state.periodStatementsError),
};

export const STATUS = {
  PENDING: 'pending',
  FULFILLED: 'fulfilled',
  REJECTED: 'rejected',
  DEFAULT: 'default',
};

const createThunk = (type, serviceMethod) =>
  createAsyncThunk(`${BILLING_MODULE_KEY}/${type}`, (data) => billingService[serviceMethod](data));

const isStatsError = (err) => !!err && typeof err === 'object' && 'status' in err && 'message' in err;

/**
 * `StatsUsage` for `{ startDate, endDate, mode, tz }`. Rejects with a
 * structured `{ status, message, field }` payload, so the card can tell a
 * busy 503 from an invalid 400.
 */
const getUsage = createAsyncThunk(`${BILLING_MODULE_KEY}/getUsage`, async (query, { rejectWithValue }) => {
  try {
    return await billingService.getUsage(query);
  } catch (err) {
    return rejectWithValue(isStatsError(err) ? err : toStatsError(err));
  }
});

const getPeriodStatements = createAsyncThunk(
  `${BILLING_MODULE_KEY}/getPeriodStatements`,
  async (query, { rejectWithValue }) => {
    try {
      return await billingService.getPeriodStatements(query);
    } catch (err) {
      return rejectWithValue(err?.message ?? String(err));
    }
  }
);

export const actions = {
  getBillingProfile: createThunk('getBillingProfile', 'getBillingProfile'),
  createBillingProfile: createThunk('createBillingProfile', 'createBillingProfile'),
  getPaymentCard: createThunk('getPaymentCard', 'getPaymentCard'),
  getPaymentCardUpdateLink: createThunk('getPaymentCardUpdateLink', 'getPaymentCardUpdateLink'),
  requestCustomPlan: createThunk('requestCustomPlan', 'requestCustomPlan'),
  getCurrentSubscription: createThunk('getCurrentSubscription', 'getCurrentSubscription'),
  getTariffs: createThunk('getTariffs', 'getTariffs'),
  createSubscription: createThunk('createSubscription', 'createSubscription'),
  upgradeSubscription: createThunk('upgradeSubscription', 'upgradeSubscription'),
  deposit: createThunk('deposit', 'deposit'),
  downgradeSubscription: createThunk('downgradeSubscription', 'downgradeSubscription'),
  cancelSubscription: createThunk('cancelSubscription', 'cancelSubscription'),
  renewSubscription: createThunk('renewSubscription', 'renewSubscription'),
  payInvoice: createThunk('payInvoice', 'payInvoice'),
  getInvoices: createThunk('getInvoices', 'getInvoices'),
  getInvoicesPage: createThunk('getInvoicesPage', 'getInvoicesPage'),
  getUsage,
  getPeriodStatements,
  clearError: createAction(`${BILLING_MODULE_KEY}/clearError`),
  clearStatus: createAction(`${BILLING_MODULE_KEY}/clearStatus`),
  clearPaymentData: createAction(`${BILLING_MODULE_KEY}/clearPaymentData`),
};

const ACTION_CONFIGS = [
  { action: actions.getBillingProfile, key: 'getBillingProfile', target: 'data' },
  { action: actions.createBillingProfile, key: 'createBillingProfile', target: 'data' },
  { action: actions.getPaymentCard, key: 'getPaymentCard', target: 'paymentCard' },
  {
    action: actions.getPaymentCardUpdateLink,
    key: 'getPaymentCardUpdateLink',
    target: null,
    onFulfilled: (state, payload) => {
      state.paymentData = { paymentUrl: payload?.url };
    },
  },
  { action: actions.requestCustomPlan, key: 'requestCustomPlan', target: null },
  {
    action: actions.getCurrentSubscription,
    key: 'getCurrentSubscription',
    target: null,
    onFulfilled: (state, payload) => {
      state.subscription = payload.subscription;
      state.serverTime = payload.serverTime;
    },
  },
  {
    action: actions.getTariffs,
    key: 'getTariffs',
    target: null,
    onFulfilled: (state, payload) => {
      state.tariffCatalog = {
        meta: payload?.meta ?? { rows: [] },
        tariffs: payload?.tariffs ?? [],
      };
    },
  },
  { action: actions.createSubscription, key: 'createSubscription', target: 'paymentData' },
  { action: actions.upgradeSubscription, key: 'upgradeSubscription', target: 'paymentData' },
  { action: actions.deposit, key: 'deposit', target: 'paymentData' },
  { action: actions.downgradeSubscription, key: 'downgradeSubscription', target: null },
  { action: actions.cancelSubscription, key: 'cancelSubscription', target: null },
  { action: actions.renewSubscription, key: 'renewSubscription', target: null },
  { action: actions.payInvoice, key: 'payInvoice', target: 'paymentData' },
  { action: actions.getInvoices, key: 'getInvoices', target: 'invoices' },
  { action: actions.getInvoicesPage, key: 'getInvoicesPage', target: 'invoicesPage' },
];

const billingSlice = createSlice({
  name: BILLING_MODULE_KEY,
  initialState: {
    data: null,
    paymentCard: null,
    subscription: null,
    serverTime: null,
    tariffCatalog: { meta: { rows: [] }, tariffs: [] },
    invoices: null,
    invoicesPage: null,
    // Usage has its own LIVE/TEST switch, so only the latest request may land.
    usage: null,
    usageError: null,
    usageRequestId: null,
    periodStatements: null,
    periodStatementsError: null,
    periodStatementsRequestId: null,
    paymentData: null,
    error: null,
    status: {
      ...Object.fromEntries(ACTION_CONFIGS.map(({ key }) => [key, STATUS.DEFAULT])),
      getUsage: STATUS.DEFAULT,
      getPeriodStatements: STATUS.DEFAULT,
    },
  },

  extraReducers: (builder) => {
    ACTION_CONFIGS.forEach(({ action, key, target, onFulfilled }) => {
      builder
        .addCase(action.pending, (state) => {
          state.status[key] = STATUS.PENDING;
          state.error = null;
        })
        .addCase(action.fulfilled, (state, { payload }) => {
          if (onFulfilled) onFulfilled(state, payload);
          else if (target) state[target] = payload;
          state.status[key] = STATUS.FULFILLED;
        })
        .addCase(action.rejected, (state, { error }) => {
          state.error = error?.message;
          state.status[key] = STATUS.REJECTED;
        });
    });

    builder
      .addCase(getUsage.pending, (state, { meta }) => {
        state.usage = null;
        state.usageError = null;
        state.usageRequestId = meta.requestId;
        state.status.getUsage = STATUS.PENDING;
      })
      .addCase(getUsage.fulfilled, (state, { payload, meta }) => {
        if (meta.requestId !== state.usageRequestId) return;
        state.usage = payload;
        state.status.getUsage = STATUS.FULFILLED;
      })
      .addCase(getUsage.rejected, (state, { payload, error, meta }) => {
        if (meta.requestId !== state.usageRequestId) return;
        state.usageError = payload ?? { status: null, message: error?.message ?? null, field: null };
        state.status.getUsage = STATUS.REJECTED;
      });

    builder
      .addCase(getPeriodStatements.pending, (state, { meta }) => {
        state.periodStatements = null;
        state.periodStatementsError = null;
        state.periodStatementsRequestId = meta.requestId;
        state.status.getPeriodStatements = STATUS.PENDING;
      })
      .addCase(getPeriodStatements.fulfilled, (state, { payload, meta }) => {
        if (meta.requestId !== state.periodStatementsRequestId) return;
        state.periodStatements = payload;
        state.status.getPeriodStatements = STATUS.FULFILLED;
      })
      .addCase(getPeriodStatements.rejected, (state, { payload, error, meta }) => {
        if (meta.requestId !== state.periodStatementsRequestId) return;
        state.periodStatementsError = payload ?? error?.message ?? null;
        state.status.getPeriodStatements = STATUS.REJECTED;
      });

    builder
      .addCase(actions.clearError, (state) => {
        state.error = null;
      })
      .addCase(actions.clearStatus, (state, { payload }) => {
        state.status[payload] = null;
      })
      .addCase(actions.clearPaymentData, (state) => {
        state.paymentData = null;
        state.status.createSubscription = STATUS.DEFAULT;
        state.status.upgradeSubscription = STATUS.DEFAULT;
        state.status.deposit = STATUS.DEFAULT;
        state.status.payInvoice = STATUS.DEFAULT;
      });
  },
});

export default billingSlice.reducer;
